import { Mail } from "lucide-react";
import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { LANDING } from "@/constants/landingTestIds";

const LAST_UPDATED = "February 9, 2026";

const DataDeletionPage = () => {
  useDocumentMeta(
    "Data Deletion Request | Tracre One",
    "Learn how to request deletion of your Tracre One account and data, what gets deleted, processing timelines, and what may be retained for legal purposes."
  );

  return (
    <LegalLayout
      title="Data Deletion Request"
      lastUpdated={LAST_UPDATED}
      testId={LANDING.dataDeletionPage}
    >
      <p className="text-slate-600 leading-relaxed">
        You are always in control of your data. Tracre One stores most of your CRM data
        locally on your device, and you can request full deletion of any data associated
        with your account at any time. This page explains how to submit a request, what gets
        deleted, how long it takes, and what limited data we may retain for legal reasons.
      </p>

      <LegalSection title="1. How to Request Account Deletion">
        <p>To request deletion of your Tracre One account and associated data:</p>
        <ul>
          <li>
            Send an email to{" "}
            <a href="mailto:support@tracreoneai.com?subject=Data Deletion Request">
              support@tracreoneai.com
            </a>{" "}
            with the subject line "Data Deletion Request".
          </li>
          <li>Include your registered mobile number and/or email address used with the App.</li>
          <li>
            Optionally, tell us the reason for deletion — this helps us improve the App (not
            required to process your request).
          </li>
        </ul>
        <a
          href="mailto:support@tracreoneai.com?subject=Data Deletion Request"
          data-testid={LANDING.dataDeletionEmailBtn}
          className="inline-flex items-center gap-2 mt-3 rounded-lg bg-[#2563EB]/10 text-[#2563EB] font-semibold px-4 py-2.5 hover:bg-[#2563EB]/15 transition-colors"
        >
          <Mail className="w-4 h-4" /> support@tracreoneai.com
        </a>
      </LegalSection>

      <LegalSection title="2. What Data Will Be Deleted">
        <p>Once your request is verified and processed, we permanently delete:</p>
        <ul>
          <li>Your account profile (name, email, phone number, business/agency details).</li>
          <li>All lead, client, and pipeline data associated with your account synced with us.</li>
          <li>Property photos, documents, or files you uploaded through the App.</li>
          <li>Any AI interaction logs tied to your account.</li>
        </ul>
        <p>
          Additionally, uninstalling the App from your device immediately removes all
          locally stored CRM data on that device.
        </p>
      </LegalSection>

      <LegalSection title="3. Processing Time">
        <p>
          We process and confirm all verified data deletion requests within{" "}
          <strong>7 business days</strong> of receipt. You will receive a confirmation email
          at the address you contacted us from once the deletion is complete.
        </p>
      </LegalSection>

      <LegalSection title="4. Data That May Be Retained for Legal Purposes">
        <p>
          In limited circumstances, we may retain certain information even after a deletion
          request, where required to:
        </p>
        <ul>
          <li>Comply with applicable tax, accounting, or legal record-keeping obligations.</li>
          <li>Resolve disputes, enforce our Terms &amp; Conditions, or prevent fraud/abuse.</li>
          <li>Maintain anonymized or aggregated data that no longer identifies you.</li>
        </ul>
        <p>
          Any such retained data is kept only for as long as legally necessary and is not used
          for any other purpose.
        </p>
      </LegalSection>

      <LegalSection title="5. Contact Us">
        <p>
          For any questions about this process or the status of your request, reach out to
          us at{" "}
          <a href="mailto:support@tracreoneai.com">support@tracreoneai.com</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default DataDeletionPage;
